/**
 * useAutoPull — read + update the scheduled auto-pull config for one bank
 * resource (paths / landers / offers). One query backs every resource's card;
 * a PUT re-arms/re-schedules a single resource and refreshes the shared cache.
 * For Media Devoted | Created By James Alexandrov
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AutoPullResource,
  TrackerId,
  BankAutoPullConfig,
  LanderBrandDefaults,
} from "@trafficpulse/shared";

import api from "@/services/api";
import { autoPullTarget } from "@/features/bank-auto-pull/tracker/auto-pull-target";

export const autoPullKeys = { all: ["banks", "auto-pull"] as const };

interface UpdateBody {
  enabled: boolean;
  intervalMin: number;
}

function fetchConfigs(url: string): Promise<BankAutoPullConfig[]> {
  return api
    .get<{ data: BankAutoPullConfig[] }>(url)
    .then((r) => r.data.data);
}

export function useAutoPull(resource: AutoPullResource, tracker?: TrackerId) {
  const target = autoPullTarget(tracker);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: target.key,
    queryFn: () => fetchConfigs(target.url),
    staleTime: 2_000,
    // Poll so the UI notices a scheduled pull starting/finishing promptly (a
    // paths pull can run 2–3 min). A short idle interval keeps the "pulling now"
    // status from appearing late (mid-pull).
    refetchInterval: (q) =>
      q.state.data?.some((c) => c.runStartedAt) ? 2_000 : 4_000,
  });

  const update = useMutation({
    mutationFn: (body: UpdateBody) =>
      api.put(`${target.url}/${resource}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: target.key }),
  });

  // Persist the network-rules pull currency (used by the manual pull page + the
  // scheduled auto-pull). Resource-agnostic — the endpoint targets network-rules.
  const setPullCurrency = useMutation({
    mutationFn: (currency: string) =>
      api.put(`${target.url}/network-rules/pull-currency`, { currency }),
    onSuccess: () => qc.invalidateQueries({ queryKey: target.key }),
  });

  // Persist the lander-pull brand defaults. Stored rather than sent per-pull so
  // the scheduler and the queue's sync-now — neither of which sends a body —
  // seed brands the same way the manual pull page does.
  const setBrandDefaults = useMutation({
    mutationFn: (body: LanderBrandDefaults) =>
      api.put(`${target.url}/landers/brand-defaults`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: target.key }),
  });

  return {
    config: query.data?.find((c) => c.resource === resource) ?? null,
    isLoading: query.isLoading,
    update,
    setPullCurrency,
    setBrandDefaults,
  };
}
